import { approvedImage } from '../marketing-media';

export type ResearchMedia = {
  description: string;
  src: string;
  type: 'image';
};

export interface ResearchPaper {
  description: string;
  href: string;
  id: string;
  media: ResearchMedia;
  title: string;
}

export interface ResearchPageContent {
  contact: {
    buttonLabel: string;
    label: string;
    title: {
      emphasized: string;
      prefix: string;
      suffix: string;
    };
  };
  intro: {
    label: string;
    paragraphs: [string, string];
    title: {
      emphasized: string;
      prefix: string;
      suffix: string;
    };
  };
  measurement: {
    description: string;
    title: string;
  };
  papers: {
    description: string;
    items: ResearchPaper[];
    title: string;
  };
  scaling: {
    description: string;
    paragraphs: [string, string];
    title: string;
  };
}

export const researchPageContent = {
  intro: {
    label: 'Research',
    title: {
      prefix: 'We’re building',
      emphasized: 'kinematic world models',
      suffix: 'for the physical AI era.',
    },
    paragraphs: [
      'Cartwheel is a frontier AI research lab working on kinetic intelligence: models that perceive, generate, and understand how bodies and objects move through and interact in space.',
      'We operate natively in 4D. We build the models, the datasets, and the compute that make embodied and physical AI systems tractable at scale.',
    ],
  },
  scaling: {
    title: 'The scaling law of movement',
    description: 'Kinematics are the next data modality to scale.',
    paragraphs: [
      'Every modality AI has learned to model followed the same pattern: as training data and compute scaled, model performance improved predictably — a scaling law. That pattern is why language models had their moment, then image models, then audio and video.',
      "We've found that human motion scales in a similar way. Trained on the largest human motion corpus assembled to date, our research shows a compute-optimal scaling law in the same scaling regime as language models — but 300x more efficient. Kinematics is the next modality to scale.",
    ],
  },
  measurement: {
    title: 'Measuring motion',
    description:
      "Next, we're building the benchmarks to prove which moves matter: a rigorous, physics-aware standard for human motion generation the field doesn't have yet.",
  },
  papers: {
    title: 'Our papers',
    description: 'Read our latest published research.',
    items: [
      {
        id: 'compute-optimal-scaling-laws',
        title: 'Compute-Optimal Scaling Laws for Human Motion Generation',
        href: 'https://github.com/cartwhl/scaling-laws-for-motion',
        description: 'How motion data scales with compute and model size, and…',
        media: approvedImage(
          '01_research_our-papers_scaling-laws.png',
          'Scaling laws and motion dataset comparison from the paper.',
        ),
      },
    ],
  },
  contact: {
    label: 'Get in contact',
    title: {
      prefix: 'Custom ',
      emphasized: 'models, datasets, research & evals',
      suffix: '.',
    },
    buttonLabel: 'Talk to us',
  },
} satisfies ResearchPageContent;
